/**
 * The social product's limits, by name (lane BOOUX1; ALGOBOO2's sixteen "not yet" items A2-1 to A2-16, docs/BACKLOG.md
 * §18, where each item's disposition is written). What is not done says so the same way everywhere: a read carries
 * `{ code, words }` where the figure would be -- never a zero, an empty list or a claim -- and a write that asks for it
 * is refused with its code. `title` is the heading a page gives it, `words` the sentence a person reads, `item` the
 * backlog row. Built instead of refused: follower fees (A2-5), the rule of following for real (A2-6), a remix of any
 * declared setting (A2-7), the fork block where recorded (A2-8), what followed a refusal (A2-9), promotions in the story
 * (A2-10), the story's delivery (A2-11), the challenge's card and note (A2-15), and its published conditions (A2-16).
 */
import { z } from 'zod'
import { ProductLimitCode } from '../refusals.js'

export interface ProductLimitCopy {
  item: `A2-${number}`
  title: string
  words: string
}

export const PRODUCT_LIMITS: Record<ProductLimitCode, ProductLimitCopy> = {
  FIRST_MENTION_PRIVATE: {
    item: 'A2-1',
    title: 'First mention not shown',
    words:
      'How early this trade was against the first public mention of its token is not shown: the calls the platform follows come from chats it keeps private. These are the times of the decision itself.',
  },
  RUNNER_LIVENESS_NOT_SERVED: {
    item: 'A2-2',
    title: 'Awake right now: not known',
    words:
      'Whether its program is running this very second is not something the trading platform tells apps. What is shown is the state the platform keeps for its strategy and why it holds, if it does.',
  },
  TRADES_OUTSIDE_APP_NOT_PUBLISHED: {
    item: 'A2-3',
    title: 'Only algo.boo’s trades',
    words:
      'Only the trades algo.boo’s agents placed through algo.boo are shown. The trading platform shows each app its own trades and no one else’s, so trades of other strategies are not listed here.',
  },
  SHADOW_DELAY_MOVE_NOT_REPLAYED: {
    item: 'A2-4',
    title: 'Price move during your delay: not simulated',
    words:
      'The simulation copies each trade at the price the agent got plus the slip real trades of that kind show; it does not replay how the price moved while your copy waited. A copy later than the agent lets its own trades trail is refused as too late.',
  },
  REMIX_PROGRAM_LINE_NOT_OFFERED: {
    item: 'A2-7',
    title: 'New rules are written at the desk',
    words:
      'A remix changes the settings the agent’s program declares. A new rule that is not a setting is a new line of its program, which the desk writes with you and checks before it runs.',
  },
  FORK_BLOCK_UNKNOWN: {
    item: 'A2-8',
    title: 'Fork block not recorded',
    words: 'The trading platform could not read the chain’s newest block when this copy was made, so the block is not known. The time of the copy stands, and both records count from it.',
  },
  SCOUT_INPUTS_NOT_RECORDED: {
    item: 'A2-12',
    title: 'Which scout list: not recorded',
    words:
      'Which of its scouts’ lists a trade passed is not recorded with the trade. The trade’s own record is shown; the scouts are shown with their own results.',
  },
  RULE_REPLAY_NOT_SERVED: {
    item: 'A2-13',
    title: 'Not replayed under a price rule',
    words:
      'What last week’s trades would have done under a rule about prices (a stop, a take-profit, slippage, how far it chases) needs each trade replayed along the prices that followed, which the platform does not do for apps. Sizes, position caps and holding times are shown from the records.',
  },
  INTRADAY_EQUITY_NOT_RECORDED: {
    item: 'A2-14',
    title: 'Drops within a day: not seen',
    words:
      'The worst drop is measured between one reading a day of the realized result; open positions are not marked to a price. A fall and recovery within one day is not seen.',
  },
  SHARED_PAPER_ACCOUNT_NOT_RUN: {
    item: 'A2-16',
    title: 'Same play money, applied',
    words:
      'Each agent trades its own play money at its own size; the board applies each agent’s return to the challenge’s amount. They are not run side by side on accounts of that exact size.',
  },
}

/** One limit where a figure would be: its code and the sentence a person reads. */
export const ProductLimit = z.object({ code: ProductLimitCode, words: z.string() })
export type ProductLimit = z.output<typeof ProductLimit>

/** The limit of `code`, as a read carries it. */
export const productLimit = (code: ProductLimitCode): ProductLimit => ({ code, words: PRODUCT_LIMITS[code].words })
